import type { ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Check, Clock, Loader2, Sparkles } from 'lucide-react';
import { fadeIn, fadeInUp, staggerContainer, warmEase } from '@/shared/motion/motionPresets';
import type { CareGuideLoadingCopy, CareGuidePanelItem } from './careGuideLoadingCopy';

type CareGuideLoadingVisualProps = {
  copy: CareGuideLoadingCopy;
  phaseLabel: string;
  /** Bar target percentage — eases toward per-phase targets, 100 on ready. */
  progressTarget: number;
  isReady: boolean;
  /** Optional inline notice (FP engagement retry). */
  notice?: ReactNode;
};

const PanelColumn = ({ heading, items }: { heading: string; items: CareGuidePanelItem[] }) => (
  <div className="flex flex-col gap-4 text-left">
    <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-sage">{heading}</div>
    {items.map(({ icon: Icon, title, body }) => (
      <div key={title} className="grid grid-cols-[34px_1fr] items-start gap-3">
        <span className="inline-flex h-[34px] w-[34px] items-center justify-center rounded-[9px] bg-sage-bg text-sage">
          <Icon className="h-4 w-4" aria-hidden />
        </span>
        <div>
          <div className="text-sm font-medium text-text-primary">{title}</div>
          <p className="text-xs leading-relaxed text-text-secondary">{body}</p>
        </div>
      </div>
    ))}
  </div>
);

/**
 * Presentational "Guided" loading layout, mapped from the prototype's
 * CareGuideGuided variant (docs/prototypes): centered header with pulsing mark,
 * progress block, and the two-column "What you'll get / How to use it" panel.
 */
export const CareGuideLoadingVisual = ({
  copy,
  phaseLabel,
  progressTarget,
  isReady,
  notice,
}: CareGuideLoadingVisualProps) => {
  const reduceMotion = useReducedMotion();

  return (
    <motion.div
      variants={staggerContainer}
      initial="initial"
      animate="animate"
      className="mx-auto flex min-h-[calc(100vh-72px)] lg:min-h-screen w-full max-w-3xl flex-col items-center justify-center px-6 py-10 text-center"
    >
      <motion.span
        variants={fadeInUp}
        animate={isReady && !reduceMotion ? { scale: [0.9, 1.06, 1] } : undefined}
        transition={{ duration: 0.4, ease: warmEase }}
        className={`relative mb-4 inline-flex h-[60px] w-[60px] items-center justify-center rounded-full transition-colors duration-300 ${
          isReady ? 'bg-sage text-white' : 'bg-sage-bg text-sage'
        }`}
      >
        {/* Pulsing halo while building (prototype cg-ring, 1.8s loop). */}
        {!isReady && !reduceMotion && (
          <motion.span
            aria-hidden
            className="absolute -inset-1.5 rounded-full border-2 border-sage/30"
            animate={{ scale: [0.9, 1.25], opacity: [0.7, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: warmEase }}
          />
        )}
        {isReady ? (
          <Check className="h-6 w-6" aria-hidden />
        ) : (
          <Sparkles className="h-6 w-6" aria-hidden />
        )}
      </motion.span>

      <motion.div
        variants={fadeInUp}
        className="mb-3 text-xs font-semibold uppercase tracking-[0.12em] text-sage"
      >
        Navigator
      </motion.div>

      {/* Keyed enter-only swaps (no exit hold): reads as a crossfade and stays
          robust under test fake-timers. */}
      <motion.h1
        key={isReady ? 'title-ready' : 'title-building'}
        variants={fadeIn}
        initial="initial"
        animate="animate"
        className="mb-2 font-termina text-[28px] font-medium leading-tight text-text-primary [text-wrap:pretty]"
      >
        {isReady ? copy.titleReady : copy.titleBuilding}
      </motion.h1>
      <motion.p
        key={isReady ? 'sub-ready' : 'sub-building'}
        variants={fadeIn}
        initial="initial"
        animate="animate"
        className="mb-6 max-w-[520px] text-[15px] leading-relaxed text-text-secondary [text-wrap:pretty]"
      >
        {isReady ? copy.subtitleReady : copy.subtitleBuilding}
      </motion.p>

      <motion.div variants={fadeInUp} className="mb-7 w-full max-w-[520px]">
        <div className="h-2 w-full overflow-hidden rounded-full bg-border-subtle/60">
          {/* Sage gradient fill (prototype #0d5d4e → #1a7d6c). */}
          <motion.span
            className="block h-full rounded-full bg-gradient-to-r from-sage to-[#1a7d6c]"
            initial={{ width: '6%' }}
            animate={{ width: `${progressTarget}%` }}
            transition={
              isReady ? { duration: 0.5, ease: warmEase } : { duration: 2.2, ease: 'easeOut' }
            }
          />
        </div>
        <div className="mt-2.5 flex items-center justify-between">
          <motion.span
            key={isReady ? 'status-ready' : phaseLabel}
            variants={fadeIn}
            initial="initial"
            animate="animate"
            className="inline-flex items-center gap-2 text-[13.5px] font-medium text-text-primary"
          >
            {isReady ? (
              <>
                <span className="inline-flex h-[18px] w-[18px] items-center justify-center rounded-full bg-sage text-white">
                  <Check className="h-3 w-3" aria-hidden />
                </span>
                All set
              </>
            ) : (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin text-sage" aria-hidden />
                {phaseLabel}…
              </>
            )}
          </motion.span>
          <span className="inline-flex items-center gap-1.5 text-xs text-text-tertiary">
            {isReady ? (
              'Done'
            ) : (
              <>
                <Clock className="h-3 w-3" aria-hidden />
                About 30 seconds
              </>
            )}
          </span>
        </div>
        {notice}
      </motion.div>

      <motion.div
        variants={fadeInUp}
        className="grid w-full grid-cols-1 gap-6 rounded-2xl border border-border-subtle bg-card-base p-6 shadow-[0_1px_2px_rgba(20,30,28,0.04),0_4px_14px_rgba(20,30,28,0.04)] md:grid-cols-[1fr_1px_1fr] md:gap-7"
      >
        <PanelColumn heading="What you'll get" items={copy.getItems} />
        <div aria-hidden className="h-px w-full self-stretch bg-border-subtle md:h-auto md:w-px" />
        <PanelColumn heading="How to use it" items={copy.useItems} />
      </motion.div>

      <motion.p variants={fadeIn} className="mt-6 text-xs text-text-tertiary">
        {copy.footerNote}
      </motion.p>
    </motion.div>
  );
};
